import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CassetteTape, Maximize2, Minimize2, Pause, Play, Shuffle, X } from 'lucide-react';
import useTitle from '../lib/useTitle';
import './FunPage.css';

// Short poll keeps play/pause on the page in step with Spotify.
const POLL_MS = 5000;
const FALLBACK_STRIPES = ['#1db954', '#14b8a6', '#3b82f6'];

// Shown when Spotify isn't configured or reachable, so the page still works.
const DEMO = {
  configured: false,
  nowPlaying: {
    id: 'demo',
    title: 'Nothing on the deck yet',
    artist: 'Connect Spotify to go live',
    album: '',
    year: '',
    trackNumber: null,
    art: null,
    artSmall: null,
    url: null,
    durationMs: 214000,
    progressMs: 61000,
    isPlaying: true,
  },
  recent: [],
};

function fmt(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function ago(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const d = Math.floor(diff / 86400);
  return d === 1 ? 'yesterday' : `${d}d ago`;
}

// Pull three colours out of the album art for the label stripes.
function useArtStripes(src) {
  const [stripes, setStripes] = useState(FALLBACK_STRIPES);
  useEffect(() => {
    if (!src) {
      setStripes(FALLBACK_STRIPES);
      return undefined;
    }
    let alive = true;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const size = 24;
        const c = document.createElement('canvas');
        c.width = size;
        c.height = size;
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        // collect saturated, mid-bright pixels, then take three spread-out hues
        const px = [];
        for (let i = 0; i < data.length; i += 4) {
          const [r, g, b] = [data[i], data[i + 1], data[i + 2]];
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const sat = max === 0 ? 0 : (max - min) / max;
          const lum = (max + min) / 2;
          if (sat > 0.25 && lum > 40 && lum < 230) px.push({ r, g, b, sat });
        }
        if (px.length < 6) {
          if (alive) setStripes(FALLBACK_STRIPES); // mostly greyscale art
          return;
        }
        px.sort((a, b) => b.sat - a.sat);
        const step = Math.floor(px.length / 3);
        const pick = [px[0], px[step], px[step * 2]].map(({ r, g, b }) => `rgb(${r}, ${g}, ${b})`);
        if (alive) setStripes(pick);
      } catch {
        // cross-origin canvas not allowed: keep the current palette
      }
    };
    img.src = src;
    return () => {
      alive = false;
    };
  }, [src]);
  return stripes;
}

/* ── Tape transport ──────────────────────────────────────────────────────────
   Reels are HTML layers moved only with transforms (scale for the tape pack,
   rotate for the hub), so the compositor animates them without repainting.
   Like a real cassette the tape runs at a constant speed, so the emptier reel
   spins faster; pausing eases the reels to a stop instead of freezing them.
   The tape itself is a thin SVG line that leaves each pack on a true tangent
   and runs over the two guide rollers. Geometry is in a 300×80 box. */

const T = { w: 300, h: 80, cy: 40, lx: 46, rx: 254, rmin: 18, rmax: 35, roll: [114, 186], rollY: 61 };
const TAPE_SPEED = 46; // box units per second
const packR = (fill) => Math.sqrt(T.rmin ** 2 + fill * (T.rmax ** 2 - T.rmin ** 2));

// Point where a line from (px, py) touches the underside of a circle.
function tangentPoint(cx, r, px, py, side) {
  const dx = px - cx;
  const dy = py - T.cy;
  const d = Math.hypot(dx, dy);
  const ang = Math.atan2(dy, dx) + side * Math.acos(Math.min(1, r / d));
  return [cx + r * Math.cos(ang), T.cy + r * Math.sin(ang)];
}

function strand(rl, rr) {
  const [ax, ay] = tangentPoint(T.lx, rl, T.roll[0], T.rollY, 1);
  const [bx, by] = tangentPoint(T.rx, rr, T.roll[1], T.rollY, -1);
  const f = (n) => n.toFixed(2);
  return `M ${f(ax)} ${f(ay)} L ${T.roll[0]} ${T.rollY} L ${T.roll[1]} ${T.rollY} L ${f(bx)} ${f(by)}`;
}

const pctX = (x) => `${(x / T.w) * 100}%`;
const REEL_SIZE = `${((2 * T.rmax) / T.w) * 100}%`;

function Reel({ x, set, side }) {
  return (
    <div className="reel" style={{ left: pctX(x), width: REEL_SIZE }}>
      <div className="reel__pack" ref={set(`pack${side}`)}>
        <div className="reel__sheen" ref={set(`sheen${side}`)} />
      </div>
      <div className="reel__hub" ref={set(`hub${side}`)}>
        <svg viewBox="-15 -15 30 30">
          <circle r="15" className="hub__ring" />
          <circle r="9" className="hub__hole" />
          {[0, 60, 120, 180, 240, 300].map((a) => (
            <rect key={a} x="-1.5" y="-9.3" width="3" height="3.8" rx="0.8" className="hub__tooth" transform={`rotate(${a})`} />
          ))}
        </svg>
      </div>
    </div>
  );
}

// mode: 'play' runs at tape speed, 'wind' fast-winds while a new tape loads,
// anything else eases to a stop.
function Transport({ pct, mode }) {
  const el = useRef({});
  const live = useRef({ pct, mode });
  live.current = { pct, mode };

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const s = { speed: 0, aL: 0, aR: 0, rL: packR(1 - live.current.pct), rR: packR(live.current.pct), dL: -1, dR: -1 };
    let raf;
    let last = performance.now();

    const draw = () => {
      const n = el.current;
      // geometry only changes as the packs grow, so skip it when they are still
      if (Math.abs(s.rL - s.dL) > 0.01 || Math.abs(s.rR - s.dR) > 0.01) {
        n.packL.style.transform = `scale(${s.rL / T.rmax})`;
        n.packR.style.transform = `scale(${s.rR / T.rmax})`;
        n.tape.setAttribute('d', strand(s.rL, s.rR));
        s.dL = s.rL;
        s.dR = s.rR;
      }
      const l = `rotate(${s.aL}deg)`;
      const r = `rotate(${s.aR}deg)`;
      n.hubL.style.transform = l;
      n.sheenL.style.transform = l;
      n.hubR.style.transform = r;
      n.sheenR.style.transform = r;
    };

    const frame = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const { mode } = live.current;
      const target = reduce ? 0 : mode === 'play' ? 1 : mode === 'wind' ? 5 : 0;
      s.speed += (target - s.speed) * (1 - Math.exp(-dt * (mode === 'wind' ? 3.5 : 2)));
      if (s.speed < 0.001 && target === 0) s.speed = 0;
      const ease = 1 - Math.exp(-dt * 2.5);
      s.rL += (packR(1 - live.current.pct) - s.rL) * ease;
      s.rR += (packR(live.current.pct) - s.rR) * ease;
      const v = TAPE_SPEED * s.speed * dt;
      // tape leaves the left reel along its underside, so both turn anticlockwise
      s.aL = (s.aL - (v / s.rL) * (180 / Math.PI)) % 360;
      s.aR = (s.aR - (v / s.rR) * (180 / Math.PI)) % 360;
      draw();
      raf = requestAnimationFrame(frame);
    };
    draw();
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  const set = (k) => (node) => {
    el.current[k] = node;
  };

  return (
    <div className="transport" aria-hidden="true">
      <svg className="transport__base" viewBox={`0 0 ${T.w} ${T.h}`}>
        <defs>
          <linearGradient id="tp-glass" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.1" />
            <stop offset="38%" stopColor="#ffffff" stopOpacity="0.02" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <rect x="104" y="12" width="92" height="54" rx="7" className="transport__window" />
        <g className="transport__guides">
          <rect x="139" y="22" width="2" height="24" rx="1" />
          <rect x="149" y="22" width="2" height="24" rx="1" />
          <rect x="159" y="22" width="2" height="24" rx="1" />
        </g>
        <path ref={set('tape')} d={strand(packR(1 - pct), packR(pct))} className="transport__tape" />
        <circle cx={T.roll[0]} cy={T.rollY} r="3.4" className="transport__roller" />
        <circle cx={T.roll[1]} cy={T.rollY} r="3.4" className="transport__roller" />
        <rect x="104" y="12" width="92" height="54" rx="7" fill="url(#tp-glass)" />
      </svg>
      <Reel x={T.lx} set={set} side="L" />
      <Reel x={T.rx} set={set} side="R" />
    </div>
  );
}

const STATUS_LABEL = {
  playing: 'Now playing',
  paused: 'Paused',
  last: 'Last played',
  demo: 'Demo tape',
  loading: 'Loading tape',
};

const ERRORS = {
  needs_permission: 'Spotify needs one more permission before the deck can change songs.',
  no_device: "Spotify isn't open on any device right now.",
  premium_required: 'Spotify only allows remote control on Premium.',
  not_on_shelf: "That tape isn't on the shelf anymore.",
  not_configured: 'Spotify is not connected yet.',
  demo: 'This is a demo tape. Connect Spotify to load real ones.',
  timeout: "Spotify didn't pick that tape up. Try again?",
};

async function sendControl(body) {
  try {
    const res = await fetch('/api/spotify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => null);
    return json || { ok: false, error: 'spotify_error' };
  } catch {
    return { ok: false, error: 'spotify_error' };
  }
}

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const nextFrame = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

function Cassette({ track, status, progressMs, stripes, onToggle, onShuffle, onFull, full }) {
  const duration = track?.durationMs || 1;
  const pct = status === 'loading' ? 0 : Math.min(1, progressMs / duration);
  const playing = status === 'playing';
  const mode = status === 'loading' ? 'wind' : playing || status === 'demo' ? 'play' : 'stop';
  const serial = track?.id ? track.id.replace(/[^0-9]/g, '').slice(0, 6).padEnd(6, '0') : '000000';
  const side = track?.trackNumber && track.trackNumber % 2 === 0 ? 'B' : 'A';

  return (
    <div
      className={`deck${mode === 'play' ? ' is-playing' : ''}${status === 'loading' ? ' is-loading' : ''}`}
      style={{ '--s1': stripes[0], '--s2': stripes[1], '--s3': stripes[2] }}
    >
      <span className="deck__screw deck__screw--tl" aria-hidden="true" />
      <span className="deck__screw deck__screw--tr" aria-hidden="true" />
      <span className="deck__screw deck__screw--bl" aria-hidden="true" />
      <span className="deck__screw deck__screw--br" aria-hidden="true" />

      <div className="label">
        {/* keyed on the track so a new song slides in */}
        <div className="label__top" key={track?.id}>
          {track?.artSmall ? (
            <img className="label__art" src={track.artSmall} alt="" />
          ) : (
            <span className="label__art label__art--empty" aria-hidden="true" />
          )}
          <div className="label__text">
            <p className="label__kicker">
              <span className="eq" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              <span key={status} className="label__state">
                {STATUS_LABEL[status]}
              </span>
            </p>
            <h2 className="label__title" title={track?.title}>
              {track?.url ? (
                <a href={track.url} target="_blank" rel="noopener noreferrer">
                  {track.title}
                </a>
              ) : (
                track?.title
              )}
            </h2>
            <p className="label__artist" title={track?.artist}>
              {track?.artist}
            </p>
          </div>
          <div className="label__meta">
            <span className="label__side">SIDE {side}</span>
            <span className="label__serial">{track?.year || serial}</span>
          </div>
        </div>

        <div className="label__window">
          <span className="label__stripes label__stripes--l" aria-hidden="true">
            <i /> <i /> <i />
          </span>
          <div className="capsule">
            <Transport pct={pct} mode={mode} />
          </div>
          <span className="label__stripes label__stripes--r" aria-hidden="true">
            <i /> <i /> <i />
          </span>
        </div>

        <div
          className="tapebar"
          role="progressbar"
          aria-label="Track progress"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration / 1000)}
          aria-valuenow={Math.round((pct * duration) / 1000)}
        >
          <span className="tapebar__fill" style={{ transform: `scaleX(${pct})` }} />
          <span className="tapebar__knob" style={{ left: `${pct * 100}%` }} />
        </div>
        <div className="tapebar__times">
          <span>{fmt(pct * duration)}</span>
          <span>{fmt(duration)}</span>
        </div>
      </div>

      <div className="controls">
        <button type="button" className="controls__btn" onClick={onShuffle} aria-label="Load a random tape from the shelf">
          <Shuffle size={15} />
        </button>
        <button
          type="button"
          className="controls__btn controls__btn--main"
          onClick={onToggle}
          disabled={status === 'loading'}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          <span key={playing ? 'pause' : 'play'} className="controls__icon">
            {playing ? <Pause size={18} /> : <Play size={18} />}
          </span>
        </button>
        <button type="button" className="controls__btn" onClick={onFull} aria-label={full ? 'Exit full screen' : 'Full screen'}>
          {full ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
        </button>
      </div>
    </div>
  );
}

export default function FunPage() {
  useTitle('Fun');
  const [data, setData] = useState(null);
  const [progressMs, setProgressMs] = useState(0);
  const [pending, setPending] = useState(null); // tape being loaded
  const [override, setOverride] = useState(null); // optimistic play/pause
  const [toast, setToast] = useState(null);
  const [full, setFull] = useState(false);
  const [fit, setFit] = useState({ s: 1, rot: 0 });
  const slotRef = useRef(null);
  const frameRef = useRef(null);
  const busy = useRef(false);
  const native = useRef(false);

  // Unlisted page: keep it out of search results.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const flash = useCallback((code) => {
    setToast({ text: ERRORS[code] || "Couldn't reach Spotify. Try again in a bit.", id: Date.now() });
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4200);
    return () => clearTimeout(t);
  }, [toast]);

  const load = useCallback(async () => {
    try {
      const t0 = performance.now();
      const res = await fetch('/api/spotify', { headers: { Accept: 'application/json' } });
      const json = res.headers.get('content-type')?.includes('json') ? await res.json() : null;
      if (json && json.configured && (json.nowPlaying || json.recent?.length)) {
        // how stale the reported progress is: CDN cache age plus half the round trip
        json.lag = (Number(res.headers.get('age')) || 0) * 1000 + (performance.now() - t0) / 2;
        setData(json);
      } else if (!json || !json.configured) {
        setData(DEMO);
      }
    } catch {
      setData((d) => d || DEMO);
    }
  }, []);

  // Poll while the tab is visible; catch up immediately when it comes back.
  useEffect(() => {
    let timer;
    let alive = true;
    const tick = async () => {
      clearTimeout(timer);
      await load();
      if (alive && !document.hidden) timer = setTimeout(tick, POLL_MS);
    };
    const onVisible = () => {
      if (document.hidden) clearTimeout(timer);
      else tick();
    };
    tick();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  const server = useMemo(() => {
    if (!data) return { track: null, status: 'last', lag: 0 };
    if (data === DEMO) return { track: DEMO.nowPlaying, status: 'demo', lag: 0 };
    if (data.nowPlaying) {
      return { track: data.nowPlaying, status: data.nowPlaying.isPlaying ? 'playing' : 'paused', lag: data.lag || 0 };
    }
    return { track: data.recent[0], status: 'last', lag: 0 };
  }, [data]);

  // What the deck shows: a tape being loaded, else Spotify with any
  // optimistic play/pause layered on until Spotify catches up.
  let { track, status, lag } = server;
  if (pending) {
    track = pending;
    status = 'loading';
    lag = 0;
  } else if (override && (status === 'playing' || status === 'paused')) {
    status = override;
  }

  useEffect(() => {
    if (pending && data?.nowPlaying?.id === pending.id) setPending(null);
  }, [data, pending]);

  useEffect(() => {
    if (!pending) return undefined;
    const t = setTimeout(() => {
      setPending(null);
      flash('timeout');
    }, 12000);
    return () => clearTimeout(t);
  }, [pending, flash]);

  useEffect(() => {
    if (!override) return undefined;
    if (server.status === override) {
      setOverride(null);
      return undefined;
    }
    const t = setTimeout(() => setOverride(null), 8000);
    return () => clearTimeout(t);
  }, [override, server.status]);

  const stripes = useArtStripes(track?.art);

  // Sync progress to each response, then tick locally while playing.
  useEffect(() => {
    if (!track) return undefined;
    const duration = track.durationMs;
    let base = track.progressMs || 0;
    if (status === 'last') base = duration;
    if (status === 'playing') base = Math.min(duration, base + lag);
    setProgressMs(base);
    if (status !== 'playing' && status !== 'demo') return undefined;
    const start = Date.now();
    const t = setInterval(() => {
      const next = base + (Date.now() - start);
      setProgressMs(status === 'demo' ? next % duration : Math.min(next, duration));
    }, 250);
    // ask again as soon as the song should have ended
    const end = status === 'playing' ? setTimeout(load, Math.max(0, duration - base) + 1200) : null;
    return () => {
      clearInterval(t);
      clearTimeout(end);
    };
  }, [track, status, lag, load]);

  // Eject the cassette, swap what's on it, slide the new one in.
  const swapTape = useCallback(async (swap) => {
    const el = slotRef.current;
    if (!el || reducedMotion()) {
      swap();
      return;
    }
    const out = el.animate(
      [
        { opacity: 1, transform: 'none', filter: 'blur(0px)' },
        { opacity: 0, transform: 'translateY(26px) scale(0.96)', filter: 'blur(3px)' },
      ],
      { duration: 280, easing: 'cubic-bezier(0.55, 0, 0.75, 0.2)', fill: 'forwards' }
    );
    await out.finished.catch(() => {});
    swap();
    await nextFrame();
    el.animate(
      [
        { opacity: 0, transform: 'translateY(-30px) scale(0.96)', filter: 'blur(3px)' },
        { opacity: 1, transform: 'none', filter: 'blur(0px)' },
      ],
      { duration: 680, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
    );
    out.cancel();
  }, []);

  const loadTape = useCallback(
    async (tape) => {
      if (!tape || busy.current) return;
      if (data === DEMO) {
        flash('demo');
        return;
      }
      busy.current = true;
      setOverride(null);
      await swapTape(() => setPending(tape));
      const r = await sendControl({ action: 'play', id: tape.id });
      if (r.ok) {
        setTimeout(load, 900);
        setTimeout(load, 2600);
      } else {
        flash(r.error);
        await swapTape(() => setPending(null));
      }
      busy.current = false;
    },
    [data, flash, load, swapTape]
  );

  const shelf = useMemo(() => {
    const seen = new Set();
    return (data?.recent || []).filter((r) => r.id && !seen.has(r.id) && seen.add(r.id)).slice(0, 8);
  }, [data]);

  const shuffle = () => {
    const pool = shelf.filter((r) => r.id !== track?.id);
    if (!pool.length) {
      flash(data === DEMO ? 'demo' : 'not_on_shelf');
      return;
    }
    loadTape(pool[Math.floor(Math.random() * pool.length)]);
  };

  const toggle = async () => {
    if (status === 'demo') return flash('demo');
    if (status === 'loading' || !track) return undefined;
    if (status === 'last') return loadTape(track); // nothing in the player: put the last tape back in
    const next = status === 'playing' ? 'paused' : 'playing';
    setOverride(next);
    const r = await sendControl({ action: next === 'paused' ? 'pause' : 'resume' });
    if (!r.ok) {
      setOverride(null);
      flash(r.error);
    } else {
      setTimeout(load, 800);
    }
    return undefined;
  };

  /* ── Full screen ──────────────────────────────────────────────────────────
     The deck keeps its 500px layout and is scaled to fill the screen; on a
     portrait phone it turns sideways so the cassette runs end to end. */

  const enterFull = () => {
    setFull(true);
    const root = document.documentElement;
    if (root.requestFullscreen) {
      root
        .requestFullscreen({ navigationUI: 'hide' })
        .then(() => {
          native.current = true;
        })
        .catch(() => {});
    }
  };

  const exitFull = useCallback(async () => {
    const el = frameRef.current;
    if (el && !reducedMotion()) {
      await el
        .animate([{ opacity: 1 }, { opacity: 0, transform: `${el.style.transform} scale(0.92)` }], {
          duration: 220,
          easing: 'ease-in',
          fill: 'forwards',
        })
        .finished.catch(() => {});
    }
    if (native.current && document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    native.current = false;
    setFull(false);
    setFit({ s: 1, rot: 0 });
    el?.getAnimations().forEach((a) => a.cancel());
    const slot = slotRef.current;
    if (slot && !reducedMotion()) {
      slot.animate([{ opacity: 0, transform: 'scale(0.96)' }, { opacity: 1, transform: 'none' }], {
        duration: 420,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (!full) return undefined;
    const vv = window.visualViewport;
    const measure = () => {
      const f = frameRef.current;
      if (!f) return;
      const w = f.offsetWidth;
      const h = f.offsetHeight;
      const vw = vv?.width || window.innerWidth;
      const vh = vv?.height || window.innerHeight;
      const portrait = vh > vw * 1.1;
      const s = portrait ? Math.min((vh * 0.9) / w, (vw * 0.9) / h) : Math.min((vw * 0.88) / w, (vh * 0.8) / h);
      setFit({ s, rot: portrait ? 90 : 0 });
    };
    // first frame at natural size, then measure: the transition does the zoom in
    const raf = requestAnimationFrame(measure);
    const onKey = (e) => e.key === 'Escape' && exitFull();
    const onNative = () => {
      if (!document.fullscreenElement && native.current) {
        native.current = false;
        exitFull();
      }
    };
    window.addEventListener('resize', measure);
    vv?.addEventListener('resize', measure);
    window.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onNative);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      vv?.removeEventListener('resize', measure);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onNative);
      document.body.style.overflow = prev;
    };
  }, [full, exitFull]);

  const spinning = status === 'playing' || status === 'demo' || status === 'loading';
  const note =
    status === 'demo'
      ? "Spotify isn't connected yet, so this is a demo tape."
      : status === 'loading'
        ? 'loading tape'
        : status === 'playing'
          ? 'live from spotify'
          : status === 'paused'
            ? 'paused on spotify'
            : `last spun ${track?.playedAt ? ago(track.playedAt) : 'a while ago'}`;

  return (
    <main id="content" className="fun container">
      <header className="fun__intro">
        <p className="fun__kicker">fun</p>
        <h1 className="fun__title">On rotation</h1>
        <p className="fun__lead">Whatever is playing in my headphones right now, pressed onto tape.</p>
      </header>

      <section
        className={`fun__stage${spinning ? ' is-playing' : ''}${full ? ' is-full' : ''}`}
        style={{ '--s1': stripes[0], '--s2': stripes[1], '--s3': stripes[2] }}
        aria-live="polite"
        aria-label={full ? 'Cassette player, full screen' : undefined}
      >
        {full && (
          <button type="button" className="fun__close" onClick={exitFull} aria-label="Exit full screen">
            <X size={18} />
          </button>
        )}
        <div
          ref={frameRef}
          className="fun__frame"
          style={full ? { transform: `translate(-50%, -50%) rotate(${fit.rot}deg) scale(${fit.s})` } : undefined}
        >
          <div ref={slotRef} className="fun__slot">
            {track ? (
              <Cassette
                track={track}
                status={status}
                progressMs={progressMs}
                stripes={stripes}
                onToggle={toggle}
                onShuffle={shuffle}
                onFull={full ? exitFull : enterFull}
                full={full}
              />
            ) : (
              <div className="deck deck--loading" aria-label="Loading" />
            )}
          </div>
        </div>
        {!full && (
          <p className="fun__note">
            {status !== 'demo' && (
              <span className={`fun__dot${status === 'playing' ? ' is-on' : ''}`} aria-hidden="true" />
            )}
            {note}
          </p>
        )}
      </section>

      {shelf.length > 0 && (
        <section className="spines" aria-label="Tape shelf">
          <div className="spines__head">
            <h2 className="spines__title">Tape shelf</h2>
            <p className="spines__hint">tap a tape to load it</p>
          </div>
          <ol className="spines__list">
            {shelf.map((r, i) => {
              const onDeck = r.id === track?.id;
              return (
                <li key={r.id} style={{ '--i': i }}>
                  <button
                    type="button"
                    className={`spine${onDeck ? ' is-on' : ''}`}
                    onClick={() => !onDeck && loadTape(r)}
                    aria-label={onDeck ? `${r.title} is on the deck` : `Load ${r.title} by ${r.artist}`}
                    aria-current={onDeck || undefined}
                  >
                    {r.artSmall ? (
                      <img className="spine__art" src={r.artSmall} alt="" loading="lazy" />
                    ) : (
                      <span className="spine__art spine__art--empty" aria-hidden="true" />
                    )}
                    <span className="spine__text">
                      <span className="spine__title">{r.title}</span>
                      <span className="spine__artist">{r.artist}</span>
                    </span>
                    <span className="spine__side">
                      <span className="spine__time">{onDeck ? 'on deck' : r.playedAt ? ago(r.playedAt) : ''}</span>
                      <span className="spine__load" aria-hidden="true">
                        <CassetteTape size={16} />
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </section>
      )}

      <div className={`fun__toast${toast ? ' is-on' : ''}`} role="status" key={toast?.id}>
        {toast?.text}
      </div>
    </main>
  );
}
